import {ReactNode} from 'react';
import {createAction} from 'Shared/utilities/actionCreator';
import {prop} from 'Shared/utilities/prop';
import generateRandomNumber from 'Shared/utilities/UniqueRandomNumberGenerator';

enum SnackbarActionType {
  CREATE_SNACKBAR = 'snackbar/CREATE_SNACKBAR',
  REMOVE_SNACKBAR = 'snackbar/REMOVE_SNACKBAR',
  CLEAR_SNACKBAR = 'snackbar/CLEAR_SNACKBAR',
}

export const createSnackbar = (message: ReactNode, useAlert?: boolean) =>
  createAction(SnackbarActionType.CREATE_SNACKBAR, {message, useAlert});
export const removeSnackbar = (id: string) => createAction(SnackbarActionType.REMOVE_SNACKBAR, {id});
export const clearSnackbar = () => createAction(SnackbarActionType.CLEAR_SNACKBAR);

type Message = {
  id: string;
  description: ReactNode;
  useAlert?: boolean;
  isOpen?: boolean;
};

type SnackbarState = {
  messages: Message[];
};

const DEFAULT_STATE: SnackbarState = {messages: []};
type SnackbarAction =
  | ReturnType<typeof createSnackbar>
  | ReturnType<typeof removeSnackbar>
  | ReturnType<typeof clearSnackbar>;

export default (state: SnackbarState = DEFAULT_STATE, action: SnackbarAction): SnackbarState => {
  switch (action.type) {
    case SnackbarActionType.CREATE_SNACKBAR:
      const allIds = state.messages.map(prop('id'));
      const newId = generateRandomNumber(allIds);

      const {payload} = action;
      const {message, useAlert} = payload;

      if (useAlert!) {
        state.messages = state.messages.filter(message => !message.useAlert!); // Exclude existing alert snackbars that have not been manually closed.
      }

      return {
        ...state,
        messages: [...state.messages, {id: newId.toString(), description: message, useAlert: useAlert}],
      };
    case SnackbarActionType.REMOVE_SNACKBAR:
      return {
        ...state,
        messages: state.messages.filter(message => action.payload.id !== message.id),
      };
    case SnackbarActionType.CLEAR_SNACKBAR:
      return DEFAULT_STATE;
    default:
      return state;
  }
};
